
import { defineComponent } from "vue";
import { mapState } from "vuex";
import { v4 as uuidV4 } from "uuid";
import HomeDesktop from "@/views/home/HomeDesktop.vue";
import Message from "@/components/Message.vue";
import InactiveLandingPage from "@/views/InactiveLandingPage.vue";
import { isDemo, isMobile, isPortrait, mobileHelper, Fingerprint2 } from "@/utils";
import isobject from "isobject";

import "@/styles/icomoon/style.css";
import { State } from "./store";
import { TextsData } from "./models/QrCodeObject";
import Loader from "@/components/Loader.vue";
import getReportService from "@/services/ReportService";
import { getApiEndpoints, sendLog } from "./utils/utils";
import { TrackingEvents } from "./services/QrCodeService";

export default defineComponent({
    name: "App",
    components: {
        InactiveLandingPage,
        HomeDesktop,
        Message,
        Loader
    },
    data() {
        return {
            nonAnimatedScreens: ["Record"],
            isMobile: isMobile(),
            isPortrait: isPortrait(),
            messageOverlay: {},
            isMessage: false,
            qrcodeId: -1,
            qrCodeError: "",
            deviceId: "",
            fingerprint: "",
            apis: {
                apiUrl: getApiEndpoints().BEAR_API_ROOT,
                jwt: getApiEndpoints().API_TOKEN
            },
            processingMessage: false,
            isDatesValid: true,
            today: new Date().getTime(),
        };
    },

    async created() {
        this.delayedProcessingMessage();
    },

    async beforeMount() {
        window.addEventListener("online", this.checkNetwork);
        window.addEventListener("offline", this.onNetworkError);
        window.addEventListener("resize", this.onResize);
        window.addEventListener("orientationchange", this.onResize);
        window.addEventListener(
            "argo_click",
            e => {
                this.onClick(e);
            },
            false
        );
    },

    mounted() {
        if (!this.checkNetwork()) return;
        const searchParams = new URLSearchParams(window.location.search);
        this.qrcodeId = searchParams.get("qr");
        if (!this.qrcodeId || this.qrcodeId.length < 4) {
            this.$store.dispatch("qrcode/displayError", {
                title: "errors.qrcodeNotFoundTitle",
                text: "errors.qrcodeNotFoundText"
            });
            return;
        }

        try {
            this.$store.dispatch("auth/check");
        } catch (e) {
            this.onServerError();
        }
    },

    beforeUnmount() {
        window.removeEventListener("resize", this.onResize);
        window.removeEventListener("offline", this.onNetworkError);
        window.removeEventListener("online", this.checkNetwork);
    },

    computed: {
        ...mapState({
            authenticated: (state: State) => state.auth.authenticated,
            qrcodeObject: (state: State) => state.qrcode.qrcodeObject,
            logoB64: (state: State) => state.qrcode.logoB64,
            videoUrl: (state: State) => state.qrcode.videoUrl,
            qrcodeError: (state: State) => state.qrcode.error
        })
    },

    watch: {
        videoUrl() {
            // const videoAssetHTML = getPit().childNodes[0] as HTMLVideoElement;
            // if (typeof videoAssetHTML !== "undefined") {
            //     videoAssetHTML.src = this.videoUrl + "?v=" + uuidV4();
            //     this.$router.replace("success");
            // }
        },
        qrcodeObject: {
            immediate: true,
            async handler() {
                if (!this.qrcodeObject) {
                    return;
                }
                this.processingMessage = false;

                if (this?.qrcodeObject?.project.name?.length) {
                    document.title = this.qrcodeObject.project.name;
                }
                if( this?.qrcodeObject?.project.scheduledStartDt?.length &&
                    this?.qrcodeObject?.project.scheduledEndDt?.length
                ) {
                    const startDt = new Date(this.qrcodeObject.project.scheduledStartDt).getTime();
                    const endDt = new Date(this.qrcodeObject.project.scheduledEndDt).getTime()
                    this.isDatesValid =
                        this.today >= startDt &&
                        this.today <= endDt;
                }
                /* Set custom Favicon */
                if (this?.qrcodeObject?.template.landingFaviconDataurl?.length) {
                    const link: HTMLLinkElement =
                        document.querySelector("link[rel*='icon']") ||
                        document.createElement("link");
                    link.type = "image/x-icon";
                    link.rel = "shortcut icon";
                    link.href = this.qrcodeObject.template.landingFaviconDataurl;
                    document.getElementsByTagName("head")[0].appendChild(link);
                }
                /* Set custom colors */
                if (this.qrcodeObject?.template.landingColors?.length) {
                    try {
                        this.computeCustomColors(
                            JSON.parse(this.qrcodeObject.template.landingColors)
                        );
                    } catch (e) {
                        console.warn("Wrong colors override value");
                    }
                }

                /**
                 * Merge default locales with custom ones
                 */
                const texts = JSON.stringify(this?.qrcodeObject?.texts as TextsData);
                if (texts?.length) {
                    try {
                        const overrides = JSON.parse(texts);
                        const locales = this.$i18n.availableLocales;

                        locales.forEach(locale => {
                            const newLocalesMessages = JSON.parse(
                                JSON.stringify(this.$i18n.getLocaleMessage(locale))
                            );
                            if (!isobject(overrides[locale])) {
                                console.warn(
                                    `Wrong text override format, overrides[${locale}] is not an object`
                                );
                                return;
                            }

                            const categories = Object.keys(overrides[locale]);

                            categories.forEach(category => {
                                if (typeof overrides[locale][category] === "undefined") {
                                    console.warn(
                                        `Wrong text override format, overrides[${locale}][${category}] is not an object`
                                    );
                                    return;
                                }
                                if (isobject(overrides[locale][category])) {
                                    const keys = Object.keys(overrides[locale][category]);
                                    keys.forEach(key => {
                                        if (!overrides[locale][category][key]) return;

                                        newLocalesMessages[category][key] =
                                            overrides[locale][category][key];
                                    });
                                }
                            });

                            this.$i18n.setLocaleMessage(locale, newLocalesMessages);
                        });
                    } catch (e) {
                        console.warn("Wrong text override value", e);
                    }
                }
                document.body.classList.add("loaded");
            }
        },
        qrcodeError() {
            if (this.qrcodeError?.title?.length && this.qrcodeError?.text?.length) {
                this.showMessage(
                    true,
                    this.$t(this.qrcodeError.title),
                    this.$t(this.qrcodeError.text)
                );
            } else {
                this.isMessage = false;
            }
        }
    },

    methods: {
        onClick(e) {
            const isTestQrId = (this.qrcodeId as string).endsWith("test");

            if (!isTestQrId) {
                // this.sendLog(5);
            }
        },
        goHome() {
            this.$router.push("/");
        },

        onResize() {
            this.isPortrait = isPortrait();
        },

        onNetworkError() {
            this.$store.dispatch("qrcode/displayError", {
                title: "errors.networkErrorTitle",
                text: "errors.networkErrorText"
            });
        },

        checkNetwork() {
            const isOnline = window.navigator.onLine;
            if (!isOnline) {
                this.onNetworkError();
            }
            if (isOnline) {
                this.clearError();
            }
            return isOnline;
        },

        onServerError() {
            this.$store.dispatch("qrcode/displayError", {
                title: "errors.serverErrorTitle",
                text: "errors.serverErrorText"
            });
        },

        showMessage(_isError, title, text, isCompatible = true, allowCopy = false) {
            this.messageOverlay = {
                isError: _isError,
                title: title,
                text: text,
                isCompliant: isCompatible,
                allowCopy
            };
            if (_isError) {
                console.error(text);
            }

            this.isMessage = true;
        },

        clearError() {
            this.$store.dispatch("qrcode/clearError");
        },

        computeCustomColors(colors) {
            if (!colors) {
                return;
            }

            let colorOverride = document.getElementById("colors-override");
            if (colorOverride) {
                colorOverride.remove();
            }

            colorOverride = document.createElement("style");
            colorOverride.id = "colors-override";
            let style = ":root {\n";
            style += Object.keys(colors)
                .map(color => `\t--${color}: ${colors[color]} !important;`)
                .join("\n");
            style += "\n}";

            colorOverride.innerHTML = style;

            document.body.appendChild(colorOverride);
        },

        async delayedProcessingMessage() {
            try {
                await this.delay(3000);
                if (this.qrcodeObject) {
                    return;
                }
                this.processingMessage = true;
            } catch (error) {
                console.error("Error:", error);
            }
        },

        delay(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        }
    }
});
